import {ReactElement, useContext} from "react";
import {CSVBoxButton} from "../../../../../components/csvbox/CSVBoxButton";
import {
    csvBoxExtractFilename,
    csvBoxExtractRows,
    csvBoxGeneralError,
    csvBoxHasError,
    normalizeDateHeaders
} from "../../../../../global/csv_box_helpers";
import {Button} from "@zendeskgarden/react-buttons";
import {AuthContext} from "../../../../../contexts/AuthContext";

export interface ManualPropertyBudgetDataItem {
    accountName: string;
    accountNumber: string;
    [k: string]: string;
}

function extractData(values: unknown[]): ManualPropertyBudgetDataItem[] {
    const retVals: ManualPropertyBudgetDataItem[] = [];
    for(const value of values) {
        if(typeof value !== "object" || value === null) {
            continue;
        }

        const row = value as Record<string, unknown>;
        const unmappedData = row["_unmapped_data"] as Record<string, unknown> | undefined;

        if(typeof unmappedData !== "object" || unmappedData === null || unmappedData === undefined) {
            continue;
        }

        const accountName = row["Account Name"];
        const accountNumber = row["Account Number"];

        if(typeof accountName !== "string") {
            continue;
        }

        if(typeof accountNumber !== "string") {
            continue;
        }

        const normalizedDateHeaders: Record<string, string> = {};
        for(const [header, value] of Object.entries(unmappedData)) {
            const normalized = normalizeDateHeaders(header);
            if(normalized !== null) {
                normalizedDateHeaders[normalized] = String(value);
            }
        }

        retVals.push({
            accountName: accountName,
            accountNumber: accountNumber,
            ...normalizedDateHeaders
        });
    }

    return retVals;
}

export interface ManualPropertyBudgetButtonProps {
    dataUploaded: (data: ManualPropertyBudgetDataItem[], filename: string) => void;
    disabled: boolean;
}

export function ManualPropertyBudgetButton(props: ManualPropertyBudgetButtonProps): ReactElement {
    const {user, budgetYear} = useContext(AuthContext);


    function handleData(data: unknown): void {
        const rows = csvBoxExtractRows(data);
        if(rows === undefined) {
            csvBoxGeneralError("budget values");
            return;
        }

        const extracted = extractData(rows);
        if(extracted.length === 0) {
            csvBoxGeneralError("budget values");
            return;
        }

        const filename = csvBoxExtractFilename(data);

        props.dataUploaded(extracted, filename ?? "unknown");
    }

    return <CSVBoxButton
        licenseKey="k2NE5ACByUB59BwgLFKBJSuSUmxoXS"
        user={{
            userId: user?.id ?? "unknown",
            name: user?.firstName || user?.lastName ? `${user?.firstName || ""} ${user?.lastName || ""}` : "unknown",
            email: user?.email ?? "unknown",
            clientId: user?.clientId ?? "unknown",
            clientName: user?.clientName ?? "unknown",
        }}
        environment={{
            // This gets set based on the environment to facilitate validation callbacks.
            "base_url": process.env["REACT_APP_CSV_BOX_BASE_URL"],
            "budget_year": budgetYear
        }}
        onImport={(result, data) => {
            if(!csvBoxHasError(result, data)) {
                handleData(data);
            } else {
                csvBoxGeneralError("budget values");
            }
        }}
        render={(launch, isLoading) => {
            return <Button onClick={launch} disabled={isLoading || props.disabled}>Upload Budget Values</Button>;
        }}
    />;
}